import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Problematique } from '../types';
import { RiAddLine, RiEditLine, RiDeleteBinLine, RiCloseLine, RiCheckLine } from 'react-icons/ri';

const inputClass =
  'w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition';
const labelClass = 'block text-xs font-medium text-gray-600 mb-1';

const AdminProblematiques: React.FC = () => {
  const [problematiques, setProblematiques] = useState<Problematique[]>([]);
  const [newProblematique, setNewProblematique] = useState<Partial<Problematique>>({
    nom: '',
    image: '',
  });
  const [editingProblematique, setEditingProblematique] = useState<Problematique | null>(null);

  useEffect(() => {
    fetchProblematiques();
  }, []);

  const fetchProblematiques = async () => {
    try {
      const res = await api.get('/problematiques/');
      setProblematiques(res.data);
    } catch (error) {
      console.error('Error fetching problematiques:', error);
    }
  };

  const handleAddProblematique = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingProblematique) {
        await api.put(`/problematiques/${editingProblematique.id}`, newProblematique);
      } else {
        await api.post('/problematiques/', newProblematique);
      }
      setNewProblematique({ nom: '', image: '' });
      setEditingProblematique(null);
      fetchProblematiques();
    } catch (error) {
      console.error('Error saving problematique:', error);
      alert('Erreur lors de la sauvegarde de la problématique.');
    }
  };

  const handleDeleteProblematique = async (id: number) => {
    if (window.confirm('Supprimer cette problématique ?')) {
      try {
        await api.delete(`/problematiques/${id}`);
        fetchProblematiques();
      } catch (error) {
        console.error('Error deleting problematique:', error);
        alert('Erreur lors de la suppression de la problématique.');
      }
    }
  };

  const editProblematique = (prob: Problematique) => {
    setEditingProblematique(prob);
    setNewProblematique({
      nom: prob.nom,
      image: prob.image || '',
    });
  };

  const cancelEdit = () => {
    setEditingProblematique(null);
    setNewProblematique({ nom: '', image: '' });
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Problématiques</h1>
        <p className="text-sm text-gray-500 mt-1">Gérez les problématiques capillaires de vos clientes.</p>
      </div>

      <div className="grid xl:grid-cols-3 gap-6 items-start">
        {/* Form */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 xl:col-span-1">
          <h2 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <RiAddLine className="text-brand-500" />
            {editingProblematique ? 'Modifier' : 'Ajouter'}
          </h2>
          <form onSubmit={handleAddProblematique} className="space-y-3">
            <div>
              <label className={labelClass}>Nom</label>
              <input
                type="text"
                placeholder="ex: Cheveux secs"
                value={newProblematique.nom}
                onChange={(e) => setNewProblematique({ ...newProblematique, nom: e.target.value })}
                className={inputClass}
                required
              />
            </div>
            <div>
              <label className={labelClass}>URL de l'image</label>
              <input
                type="text"
                placeholder="https://..."
                value={newProblematique.image || ''}
                onChange={(e) => setNewProblematique({ ...newProblematique, image: e.target.value })}
                className={inputClass}
              />
            </div>
            <div className="flex gap-2 pt-2">
              <button
                type="submit"
                className="flex-1 flex items-center justify-center gap-1.5 bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium py-2.5 rounded-lg transition"
              >
                <RiCheckLine />
                {editingProblematique ? 'Enregistrer' : 'Ajouter'}
              </button>
              {editingProblematique && (
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="px-3 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 text-sm transition"
                >
                  <RiCloseLine />
                </button>
              )}
            </div>
          </form>
        </div>

        {/* List */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden xl:col-span-2">
          <div className="px-5 py-4 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-800">
              Problématiques
              <span className="ml-2 text-xs font-normal text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">
                {problematiques.length}
              </span>
            </h3>
          </div>
          {problematiques.length === 0 ? (
            <p className="px-5 py-8 text-center text-gray-400 text-sm">Aucune problématique</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Image</th>
                    <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Nom</th>
                    <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {problematiques.map((p) => (
                    <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-3">
                        {p.image ? (
                          <img src={p.image} alt={p.nom} className="w-10 h-10 object-cover rounded-full" />
                        ) : (
                          <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center text-gray-400">
                            -
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        <span className="text-sm text-gray-800 font-medium">{p.nom}</span>
                      </td>
                      <td className="px-5 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => editProblematique(p)}
                            className="p-1.5 text-gray-400 hover:text-brand-500 hover:bg-brand-50 rounded-lg transition"
                          >
                            <RiEditLine />
                          </button>
                          <button
                            onClick={() => handleDeleteProblematique(p.id)}
                            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                          >
                            <RiDeleteBinLine />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminProblematiques;
